/* eslint-disable max-len */
import {
  Component,
  ChangeDetectionStrategy,
  AfterViewInit,
  OnInit,
  OnDestroy,
  NgZone,
  ChangeDetectorRef,
  ViewChild,
  LOCALE_ID,
  inject,
} from "@angular/core";
import {
  TaskDto,
  TaskTimePeriodsDto,
  YearPeriod,
  CalendarWeek,
  TasksClientProvider,
  TaskUpdateDateRangesCommand,
  ResourcesClientProvider,
  EnumNamesValuesDto,
  TaskStatusMetaDto,
  ResourceUserGroupDto,
  ResourceType,
  ITaskDto,
  TasksDeleteMultiCommand,
  Operation,
  ValidationResult,
  InternalServerErrorResult,
  BelsoftUsersClientProvider,
  UserAbsencesForPlanOverviewItemDto,
} from "src/app/web-api/web-api";
import {
  map,
  debounceTime,
  finalize,
  switchMap,
  catchError,
  takeUntil,
} from "rxjs/operators";
import {
  Observable,
  BehaviorSubject,
  of,
  forkJoin,
  EMPTY,
  Subject,
  merge,
} from "rxjs";
import { PlanungApiTaskCardComponent } from "./task-card/task-card.component";
import { AnlageViewModel, ResourceViewModel } from "../view-models";
import {
  NumberTaskCollection,
  NumberTaskCollectionType,
} from "./number-tasks-collection/NumberTaskCollection";
import { AlertService, MasterDataService } from "src/app/services";
import { addMonths, addDays } from "date-fns";
import { SelectionWrapper } from "./SelectionWrapper";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { UserService } from "src/app/services/user.service";
import { CstNavigationService } from "src/app/services/cst-navigation.service";
import * as jsonpatch from "fast-json-patch";
import { CstDateTimeService } from "src/app/services/cst-date-time.service";
import { orderBy } from "src/app/helpers/cst-data-query";
import { PermissionService } from "src/app/services/permission.service";
import { BFF_IsSaasInstance } from "src/app/injection-tokens/backend-feature-flags";
import { FormControl, FormGroup } from "@angular/forms";
import {
  MaterialContextMenuComponent,
  MenuAction,
} from "src/app/common-components/material-context-menu/material-context-menu.component";

enum TaskListFilterMode {
  None,
  Month,
  CalendarWeek,
  Date,
}

enum PlanViewMode {
  Months,
  CalendarWeeks,
  Calendar,
  PerformanceTest,
}

class PlanungUiState {
  private _data = {
    calendarGroupingActive: true,
    calendarViewIndex: 0,
    planView: PlanViewMode.Months,
    referenceDate: new Date(),
    referenceIndex: null,
    referenceTask: null,
    showTaskCardTooltips: true,
    hideTasksForOverview: false,
  };

  private _storage = localStorage;
  private _storageKey = "PlanungUiState";

  public changed = new BehaviorSubject<PlanungUiState>(this);

  public set CalendarGroupingActive(value: boolean) {
    this._data.calendarGroupingActive = value;
    this._SaveState();
  }
  public get CalendarGroupingActive() {
    return this._data.calendarGroupingActive;
  }
  public set CalendarViewIndex(value: number) {
    this._data.calendarViewIndex = value;
    this._SaveState();
  }
  public get CalendarViewIndex() {
    return this._data.calendarViewIndex;
  }

  public set PlanView(value: PlanViewMode) {
    this._data.planView = value;
    this._SaveState();
  }
  public get PlanView() {
    return this._data.planView;
  }

  public set ReferenceDate(value: Date) {
    this._data.referenceDate = value;
    this._SaveState();
  }
  public get ReferenceDate() {
    return this._data.referenceDate;
  }

  public set ReferenceIndex(value: number) {
    this._data.referenceIndex = value;
    this._SaveState();
  }
  public get ReferenceIndex() {
    return this._data.referenceIndex;
  }

  public set ReferenceTask(value: string) {
    this._data.referenceTask = value;
    this._SaveState();
  }
  public get ReferenceTask() {
    return this._data.referenceTask;
  }

  public set ShowTaskCardTooltips(value: boolean) {
    this._data.showTaskCardTooltips = value;
    this._SaveState();
  }
  public get ShowTaskCardTooltips() {
    return this._data.showTaskCardTooltips;
  }

  public set HideTasksForOverview(value: boolean) {
    this._data.hideTasksForOverview = value;
    this._SaveState();
  }
  public get HideTasksForOverview() {
    return this._data.hideTasksForOverview;
  }

  public constructor(private _dts: CstDateTimeService) {
    const json = JSON.parse(this._storage.getItem(this._storageKey));
    Object.assign(this._data, json);
    this._data.referenceDate = new Date(this._data.referenceDate || new Date());
  }

  private _SaveState() {
    const json = JSON.stringify(this._data);
    this._storage.setItem(this._storageKey, json);
    this.changed.next(this);
  }
}

class PlanungFilter {
  private _storageTemp = sessionStorage;
  private _storagePerm = localStorage;
  private _storageKey = "PlanungFilter";

  public changed = new Subject<PlanungFilter>();

  private _categories = [];

  public set Categories(value: string[]) {
    if (value !== null) {
      this._categories = value;
      this._SaveState();
    }
  }

  public get Categories() {
    return this._categories;
  }

  private _anlageNr = "";
  public set AnlageNr(value: string) {
    this._anlageNr = value;
    this._SaveState();
  }
  public get AnlageNr() {
    return this._anlageNr;
  }

  private _resources = [];
  public set Resources(value: string[]) {
    this._resources = value;
    this._SaveState();
  }
  public get Resources() {
    return this._resources;
  }
  private _taskStatus = 0;
  public set TaskStatus(value: number) {
    this._taskStatus = value;
    this._SaveState();
  }
  public get TaskStatus() {
    return this._taskStatus;
  }

  private _startMonth = 1;
  public set StartMonth(value: number) {
    this._startMonth = value;
    this._SaveState();
  }
  public get StartMonth() {
    return this._startMonth;
  }

  private _startYear = new Date().getFullYear();
  public set StartYear(value: number) {
    this._startYear = value;
    this._SaveState();
  }
  public get StartYear() {
    return this._startYear;
  }

  private _endMonth = 12;
  public set EndMonth(value: number) {
    this._endMonth = value;
    this._SaveState();
  }
  public get EndMonth() {
    return this._endMonth;
  }
  private _endYear = new Date().getFullYear();
  public set EndYear(value: number) {
    this._endYear = value;
    this._SaveState();
  }
  public get EndYear() {
    return this._endYear;
  }

  public set Start(value: Date) {
    this._startMonth = value.getMonth() + 1;
    this._startYear = value.getFullYear();
    this._SaveState();
  }
  public get Start() {
    const result = new Date();
    result.setUTCFullYear(this._startYear, this._startMonth - 1, 1);
    return result;
  }

  public set End(value: Date) {
    this._endMonth = value.getMonth() + 1;
    this._endYear = value.getFullYear();
    this._SaveState();
  }
  public get End() {
    const result = new Date();
    const lastDay = this._dts.GetDaysInMonth(this._endMonth - 1, this._endYear);
    result.setUTCFullYear(this._endYear, this._endMonth - 1, lastDay);
    return result;
  }

  private _taskNoText = "";
  public set TaskNoText(value: string) {
    this._taskNoText = value;
    this._SaveState();
  }
  public get TaskNoText() {
    return this._taskNoText;
  }

  public constructor(
    private _dts: CstDateTimeService,
    private _userService: UserService,
    storageKeyPostfix: string,
  ) {
    this._storageKey =
      this._userService?.CurrentUser?.mandantID ??
      "" + "_" + this._storageKey + storageKeyPostfix;
    let json = JSON.parse(this._storageTemp.getItem(this._storageKey));
    if (!json) {
      json = JSON.parse(this._storagePerm.getItem(this._storageKey));
    }
    if (!json) {
      return;
    }
    Object.assign(this, json);
  }

  apply(x: ITaskDto): boolean {
    if (this.Categories != null) {
      if (
        this.Categories.length > 0 &&
        this.Categories.indexOf(x.categoryID) < 0
      ) {
        return false;
      }
    } else {
      this.Categories = new Array<string>();
    }
    if (this.Resources?.length > 0) {
      const resToCheck = x.claimResourceID || x.assignedToResourceID || null;
      if (
        this.Resources.findIndex(
          (filterRes) =>
            filterRes === resToCheck ||
            (filterRes === "null" && resToCheck === null),
        ) < 0
      ) {
        return false;
      }
    }
    if (
      !(
        x.taskNoDisplay.includes(this.TaskNoText) ||
        (x.label &&
          x.label.toLowerCase().includes(this.TaskNoText.toLowerCase()))
      )
    ) {
      return false;
    }
    if (
      !(
        this.TaskStatus === 0 ||
        (this.TaskStatus > 0 && this.TaskStatus & x.state)
      )
    ) {
      return false;
    }
    return true;
  }

  private _SaveState() {
    if (this._categories == null) {
      this._categories = new Array<string>();
    }
    const json = JSON.stringify({
      _anlageNr: this._anlageNr,
      _categories: this._categories,
      _resources: this._resources,
      _startMonth: this._startMonth,
      _startYear: this._startYear,
      _endMonth: this._endMonth,
      _endYear: this._endYear,
      _taskNoText: this._taskNoText,
      _taskStatus: this._taskStatus,
    });
    this._storageTemp.setItem(this._storageKey, json);
    this._storagePerm.setItem(this._storageKey, json);

    this.changed.next(this);
  }
}

@Component({
  selector: "app-planung-api-overview",
  templateUrl: "./planung-api-overview.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["./planung-api-overview.component.scss"],
  standalone: false,
})
export class PlanungApiOverviewComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _userService = inject(UserService);
  private _taskClient = inject(TasksClientProvider);
  private _resClient = inject(ResourcesClientProvider);
  private _usersClient = inject(BelsoftUsersClientProvider);
  private _zone = inject(NgZone);
  private _changeDetector = inject(ChangeDetectorRef);
  private _dialogs = inject(CstDialogsService);
  private _alrt = inject(AlertService);
  private _md = inject(MasterDataService);
  private _nav = inject(CstNavigationService);
  private _perm = inject(PermissionService);
  private _dts = inject(CstDateTimeService);
  private _localeID = inject(LOCALE_ID);
  isSaasInstance = inject(BFF_IsSaasInstance);

  public destroy$ = new Subject<void>();

  public resources$ = new BehaviorSubject<ResourceViewModel[]>([]);

  TaskListFilterMode = TaskListFilterMode;
  PlanViewMode = PlanViewMode;

  private dummyDragImage: Element = null;

  @ViewChild("taskContextMenu")
  public taskContextMenu: MaterialContextMenuComponent;

  filterRangeCtl: FormGroup;
  filterAnlageCtl: FormGroup;

  public uiState: PlanungUiState;
  constructor() {
    this.uiState = new PlanungUiState(this._dts);
    this.filter = new PlanungFilter(this._dts, this._userService, "Assigned");
    this.filterUnassigned = new PlanungFilter(
      this._dts,
      this._userService,
      "Unassigned",
    );

    this.selection.selectionChanged.subscribe((items) => {
      if (items === null || items.length === 0) {
        this.uiState.ReferenceTask = null;
      } else {
        this.uiState.ReferenceTask = items[0].id;
      }
    });

    this.filterRangeCtl = new FormGroup({
      start: new FormControl(this.filter.Start),
      end: new FormControl(this.filter.End),
    });

    this.filterRangeCtl.valueChanges.subscribe((value) => {
      if (value.start) this.filter.Start = value.start;
      if (value.end) this.filter.End = value.end;
    });

    this.filterAnlageCtl = new FormGroup({
      anlage: new FormControl(this.filter.AnlageNr),
    });
    this.filterAnlageCtl.valueChanges.subscribe((value) => {
      if (value.anlage !== undefined) this.filter.AnlageNr = value.anlage;
    });

    merge(
      this.filter.changed,
      this.filterUnassigned.changed,
      this.uiState.changed,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public get DragDropKey_taskID() {
    return PlanungApiTaskCardComponent.DragDropKey_TaskID;
  }
  public get DragDropKey_tasks() {
    return "tasks";
  }

  public isLoading = new BehaviorSubject<boolean>(false);
  public tasksUnassignedObs = new BehaviorSubject<TaskDto[]>([]);
  public tasks: TaskDto[] = [];
  public resources: ResourceViewModel[] = [];
  public resUserGroups: ResourceUserGroupDto[] = [];
  public taskStates: EnumNamesValuesDto = null;
  public taskStateMetas: TaskStatusMetaDto[] = null;
  public absencesByResourceID: {
    [resourceID: string]: UserAbsencesForPlanOverviewItemDto[];
  };

  public filter: PlanungFilter;
  public filterUnassigned: PlanungFilter;
  public get FilterRangeStart() {
    return this.filter.Start;
  }
  public get FilterRangeEnd() {
    return this.filter.End;
  }

  public selection = new SelectionWrapper<TaskDto>(
    () => this.tasks,
    () => this.tasksUnassignedObs.value,
  );

  public get TasksUnassigned(): Observable<TaskDto[]> {
    return this.tasksUnassignedObs.pipe(
      map((res) => {
        return res.filter((t) => {
          if (!t.dateRangesActive) {
            return true;
          }
          if (!t.dateRanges) {
            return true;
          }
          return false;
        });
      }),
    );
  }

  public get TasksPlanned(): TaskDto[] {
    return this.tasks.filter((t) => t.dateRangesActive);
  }

  private _taskMonths: NumberTaskCollection[] = null;
  public get TasksMonths(): NumberTaskCollection[] {
    if (!this._taskMonths) {
      const result = [];
      let idx = 0;
      let date = new Date(this.filter.Start);
      let done = false;
      do {
        result.push(
          new NumberTaskCollection(
            this._dts,
            this._localeID,
            this.TasksPlanned,
            NumberTaskCollectionType.YearMonth,
            date.getFullYear(),
            date.getMonth() + 1,
          ),
        );
        done =
          date.getMonth() === this.filter.End.getMonth() &&
          date.getFullYear() === this.filter.End.getFullYear();
        date = addMonths(date, 1);
        ++idx;
        if (idx > 100) {
          break;
        }
      } while (!done);
      this._taskMonths = result;
    }
    return this._taskMonths;
  }

  public get TaskMonthIndices(): number[] {
    return this.TasksMonths.map((_, idx) => idx);
  }

  private _tasksCalendarWeeks: NumberTaskCollection[] = null;
  public get TasksCalendarWeeks(): NumberTaskCollection[] {
    if (!this._tasksCalendarWeeks) {
      const result = [];
      const startCw = this._dts.GetCalendarWeekFromDate(this.filter.Start);
      const endCw = this._dts.GetCalendarWeekFromDate(this.filter.End);

      for (
        let year = this.filter.StartYear;
        year <= this.filter.EndYear;
        ++year
      ) {
        const maxCwInYear = this._dts.GetNumberWeeksInYear(
          this.filter.StartYear,
        );
        const minCw = startCw.year === year ? startCw.week : 1;
        const maxCw = endCw.year === year ? endCw.week : maxCwInYear;
        for (let cw = minCw; cw <= maxCw; ++cw) {
          result.push(
            new NumberTaskCollection(
              this._dts,
              this._localeID,
              this.TasksPlanned.filter((t) => t.dateRangesActive),
              NumberTaskCollectionType.CalendarWeek,
              year,
              cw,
            ),
          );
        }
      }
      this._tasksCalendarWeeks = result;
    }
    return this._tasksCalendarWeeks;
  }

  public get TaskCalendarWeeksIndices(): number[] {
    return this.TasksCalendarWeeks.map((_, idx) => idx);
  }

  private _tasksDays: NumberTaskCollection[] = null;
  public get TasksDays(): NumberTaskCollection[] {
    if (!this._tasksDays) {
      const result = [];

      const ref = this.uiState.ReferenceDate;
      const startDay = new Date(
        ref.getFullYear(),
        ref.getMonth(),
        1,
        0,
        0,
        0,
        0,
      );
      const endDay = new Date(
        ref.getFullYear(),
        ref.getMonth() + 1,
        0,
        23,
        59,
        59,
        0,
      );

      let day = new Date(startDay);
      do {
        result.push(
          new NumberTaskCollection(
            this._dts,
            this._localeID,
            this.TasksPlanned,
            NumberTaskCollectionType.Day,
            null,
            null,
            day,
          ),
        );
        day = addDays(day, 1);
      } while (day < endDay);
      this._tasksDays = result;
    }
    return this._tasksDays;
  }

  public get TaskDaysIndices(): number[] {
    return this.TasksDays.map((_, idx) => idx);
  }

  public get taskContextMenuItems() {
    const result: Array<{
      text: string;
      click?: () => void;
      items?: any[];
      cssClass?: string;
      iconClass?: string;
      disabled?: boolean;
    }> = [];

    const selection = [...this.selection.Items];
    if (selection.length === 0) {
      return [];
    }

    const taskStateItems = [];
    const assignedToItems = [];

    if (selection.length === 1) {
      const taskNo = selection[0].taskNo;
      result.push({
        text: $localize`:@@PlanungAPI_Task_ContextMenu_Open:Open`,
        iconClass: "fa fa-edit",
        click: () => this.OnTaskOpen(taskNo),
      });
      result.push({ text: "", cssClass: "cst-separator" });
    }

    result.push({
      text: $localize`:@@PlanungAPI_Task_ContextMenu_State:State`,
      items: taskStateItems,
    });

    result.push({
      text: $localize`:@@PlanungAPI_Task_ContextMenu_AssignedTo:Assigned To`,
      items: assignedToItems,
    });

    result.push({ text: "", cssClass: "cst-separator" });

    result.push({
      text: $localize`:@@PlanungAPI_Task_ContextMenu_Delete:Delete`,
      iconClass: "fa fa-trash",
      click: () => this.OnTasksDelete(selection),
      disabled: !this.canTasksDelete(),
    });

    if (selection.length > 1) {
      result.push({
        text: $localize`:@@PlanungAPI_Task_ContextMenu_ChangeDueDate:Change due date`,
        iconClass: "fa fa-calendar",
        click: () => this.OnSetToBeDoneDateForMultiple(selection),
      });
    }

    forkJoin([
      this._md.Tasks.GetTaskStateMetas(),
      this._md.Resources.GetResources(),
    ]).subscribe(([taskStates, resourceResult]) => {
      taskStates.statusMetaInfos.forEach((ts) => {
        taskStateItems.push({
          text: ts.displayLabel,
          click: () => this.OnTasksPatch(selection, { state: ts.state }),
        });
      });

      assignedToItems.push({
        text: $localize`:@@PlanungAPI_Overview_FilterResources_UnassignedSelection:- Unassigned -`,
        click: () => this.SetTaskAssignedTo(selection, null, true),
      });

      const resources = orderBy(resourceResult.resources, [
        { field: "name", dir: "asc" },
      ]);
      resources.forEach((res) => {
        assignedToItems.push({
          text: res.name,
          click: () => this.SetTaskAssignedTo(selection, res.id, true),
        });
      });
    });

    return result;
  }

  currentMenuActions: MenuAction[] = [];

  public onRightClick(event: MouseEvent, item: any) {
    const selection =
      this.selection.Items.length > 0 ? [...this.selection.Items] : [item];

    forkJoin([
      this._md.Tasks.GetTaskStateMetas(),
      this._md.Resources.GetResources(),
    ]).subscribe(([taskStates, resourceResult]) => {
      this.currentMenuActions = this.buildTaskMenu(
        selection,
        taskStates,
        resourceResult.resources,
      );

      this.taskContextMenu.open(event);
    });
  }

  private buildTaskMenu(
    selection: any[],
    taskStates: any,
    resources: any,
  ): MenuAction[] {
    const result: MenuAction[] = [];

    if (selection.length === 0) {
      return [];
    }

    if (selection.length === 1) {
      const taskNo = selection[0].taskNo;
      result.push({
        label: $localize`:@@PlanungAPI_Task_ContextMenu_Open:Open`,
        icon: "edit",
        callback: () => this.OnTaskOpen(taskNo),
      });
      result.push({ divider: true });
    }

    const taskStateItems: MenuAction[] = taskStates.statusMetaInfos.map(
      (ts: any) => ({
        label: ts.displayLabel,
        callback: () => this.OnTasksPatch(selection, { state: ts.state }),
      }),
    );

    result.push({
      label: $localize`:@@PlanungAPI_Task_ContextMenu_State:State`,
      children: taskStateItems,
    });

    const assignedToItems: MenuAction[] = [];

    assignedToItems.push({
      label: $localize`:@@PlanungAPI_Overview_FilterResources_UnassignedSelection:- Unassigned -`,
      callback: () => this.SetTaskAssignedTo(selection, null, true),
    });

    const sortedResources = orderBy(resources, [{ field: "name", dir: "asc" }]);
    sortedResources.forEach((res: any) => {
      assignedToItems.push({
        label: res.name,
        callback: () => this.SetTaskAssignedTo(selection, res.id, true),
      });
    });

    result.push({
      label: $localize`:@@PlanungAPI_Task_ContextMenu_AssignedTo:Assigned To`,
      children: assignedToItems,
    });

    result.push({ divider: true });

    result.push({
      label: $localize`:@@PlanungAPI_Task_ContextMenu_Delete:Delete`,
      icon: "delete",
      callback: () => this.OnTasksDelete(selection),
      disabled: !this.canTasksDelete(),
    });

    if (selection.length >= 1) {
      result.push({
        label: $localize`:@@PlanungAPI_Task_ContextMenu_ChangeDueDate:Change due date`,
        icon: "calendar_today",
        callback: () => this.OnSetToBeDoneDateForMultiple(selection),
      });
    }

    return result;
  }

  ngOnInit() {
    forkJoin({
      types: this._md.Resources.GetTypes(),
      resources: this._md.Resources.GetResources(),
      taskStates: this._md.Tasks.GetTaskStates(),
      taskStateMetas: this._md.Tasks.GetTaskStateMetas(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ types, resources, taskStates, taskStateMetas }) => {
        this.resources = [
          new ResourceViewModel({
            name: $localize`:@@PlanungAPI_Overview_FilterResources_UnassignedSelection:- unassigned -`,
            id: "null",
          }),
          ...resources.resources.map((r) => {
            const resVM = new ResourceViewModel(r);
            resVM.typeName = types.namesValues.find(
              (nv) => nv.value === r.type,
            )?.name;
            return resVM;
          }),
        ];

        this.resources$.next(this.resources);

        const userGroups = resources.resources.filter(
          (r) => r.type === ResourceType.UserGroup,
        );

        if (userGroups.length > 0) {
          forkJoin(
            userGroups.map((grp) => this._resClient.getUserGroup(grp.id)),
          )
            .pipe(takeUntil(this.destroy$))
            .subscribe((groups) => {
              this.resUserGroups = groups;
              this._changeDetector.markForCheck();
            });
        } else {
          this.resUserGroups = [];
        }

        this.taskStates = taskStates;
        this.taskStateMetas = taskStateMetas.statusMetaInfos;

        this._changeDetector.detectChanges();
        this.ReloadTasks();
      });

    merge(this.filter.changed, this.filterUnassigned.changed)
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.ReloadTasks());

    document.body.ondragend = () => this.OnDragEndGlobal();

    this._changeDetector.detectChanges();
  }

  public ngAfterViewInit(): void {
    this.uiState.changed.subscribe(() => (this._tasksDays = null));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ReloadTasks() {
    this.isLoading.next(true);

    /// Start auf ein Monat zurück setzen, da wenn Ansicht "Kalenderwoche", die KW im vorherigen Monat anfangen könnte
    /// und dann würden die Aufgaben nicht mehr angezeigt werden.
    const start = addMonths(this.filter.Start, -1);

    const resourcesCombined = this.getCombinedUniqueValues(
      this.filter.Resources,
      this.filterUnassigned.Resources,
    );

    const categoriesCombined = this.getCombinedUniqueValues(
      this.filter.Categories,
      this.filterUnassigned.Categories,
    );

    this._taskClient
      .getTasksForPlanning(
        start,
        this.filter.End,
        resourcesCombined,
        this.filter.AnlageNr ? [this.filter.AnlageNr] : null,
        categoriesCombined,
      )
      .pipe(
        map((res) => res.data),
        switchMap((tasks) => this.loadAbsences(start, tasks)),
        finalize(() => this.isLoading.next(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ tasks, absencesByResourceID }) => {
          this.updateTasksState(tasks, absencesByResourceID);
          this._changeDetector.detectChanges();
        },
        error: (error) => {
          console.error("Failed to reload tasks:", error);
          this.clearTasksState();
        },
      });
  }

  private getCombinedUniqueValues(
    arr1: string[],
    arr2: string[],
  ): string[] | null {
    if (!arr1?.length || !arr2?.length) {
      return null;
    }
    return [...new Set([...arr1, ...arr2])];
  }

  private loadAbsences(
    start: Date,
    tasks: TaskDto[],
  ): Observable<{ tasks: TaskDto[]; absencesByResourceID: any }> {
    if (this.isSaasInstance) {
      return of({ tasks, absencesByResourceID: {} });
    }

    const resourceIDs =
      this.filter.Resources?.length > 0
        ? this.filter.Resources
        : this.resources
            .filter((x) => x.type === ResourceType.User)
            .map((x) => x.id);

    return this._usersClient
      .userAbsencesForPlanOverviewGet(
        start,
        this.filter.End,
        [],
        [],
        resourceIDs,
      )
      .pipe(
        map((absences) => ({
          tasks,
          absencesByResourceID: this.groupAbsencesByResourceID(
            absences.absences,
          ),
        })),
      );
  }

  private groupAbsencesByResourceID(
    absences: UserAbsencesForPlanOverviewItemDto[],
  ): {
    [resourceID: string]: UserAbsencesForPlanOverviewItemDto[];
  } {
    const grouped: { [key: string]: UserAbsencesForPlanOverviewItemDto[] } = {};

    for (const absence of absences) {
      if (!absence.resourceID) continue;

      if (!grouped[absence.resourceID]) {
        grouped[absence.resourceID] = [];
      }
      grouped[absence.resourceID].push(absence);
    }

    return grouped;
  }

  private updateTasksState(tasks: TaskDto[], absencesByResourceID: any): void {
    const filteredTasks = tasks?.filter((x) => this.filter.apply(x)) ?? [];
    const unassignedTasks =
      tasks?.filter((x) => this.filterUnassigned.apply(x)) ?? [];

    this.tasks = filteredTasks;
    this._taskMonths = null;
    this._tasksCalendarWeeks = null;
    this._tasksDays = null;

    this.tasksUnassignedObs.next(unassignedTasks);
    this.absencesByResourceID = absencesByResourceID;

    this._changeDetector.markForCheck();
  }

  private clearTasksState(): void {
    this.tasks = [];
    this._taskMonths = null;
    this._tasksCalendarWeeks = null;
    this._tasksDays = null;

    this.tasksUnassignedObs.next([]);

    this._changeDetector.markForCheck();
  }

  public GetTasksFromDragEvent(e: DragEvent): TaskDto[] {
    const tasksString = e.dataTransfer.getData(this.DragDropKey_tasks);
    if (!tasksString) {
      return null;
    }

    return JSON.parse(tasksString).map((x: ITaskDto) => {
      const result = new TaskDto();
      result.init(x);
      return result;
    });
  }

  public OnSetToBeDoneDateForMultiple(selection: TaskDto[]) {
    this._dialogs
      .PromptDate(
        undefined,
        $localize`:@@PlanungAPI_Tasks_PromptNewDueDate:Please enter a new due date`,
        { minDate: new Date() },
      )
      .pipe(
        map((result) => {
          if (!result) {
            return null;
          }

          const newDueDate = new Date(result);
          const noCanDo: TaskDto[] = new Array<TaskDto>();
          const canDo: TaskDto[] = new Array<TaskDto>();
          for (const task of selection) {
            if (task.todoDate != null && task.todoDate > newDueDate) {
              noCanDo.push(task);
            } else {
              canDo.push(task);
            }
          }
          if (canDo.length == 0) {
            return { cmd: [], noCanDo };
          }

          const cmd = new Array<TaskUpdateDateRangesCommand>();
          for (const task of canDo) {
            const singleCmd = new TaskUpdateDateRangesCommand();
            singleCmd.taskID = task.id;
            singleCmd.assignedToResourceID = task.assignedToResourceID;
            singleCmd.dateRangesActive = task.dateRangesActive;
            singleCmd.periodOfAction = task.dateRanges.periodOfAction;
            singleCmd.plannedWeek = task.dateRanges.plannedWeek;
            singleCmd.todoDate = task.dateRanges.todoDate;
            singleCmd.dueDate = newDueDate;
            cmd.push(singleCmd);
          }
          return { cmd, noCanDo };
        }),
        switchMap((res) => {
          if (!res) {
            return EMPTY;
          }

          if (res.cmd.length === 0) {
            return of({ updateResult: [], noCanDo: res.noCanDo });
          }

          return this._taskClient
            .updateDateRangesBulk(res.cmd)
            .pipe(
              map((updateResult) => ({ updateResult, noCanDo: res.noCanDo })),
            );
        }),
        switchMap((res) => {
          if (res.updateResult.length > 0) {
            this.ReloadTasks();
          }
          if (res.noCanDo.length === 0) {
            return EMPTY;
          }

          let message = "";
          for (const task of res.noCanDo) {
            message = message + "#" + task.taskNo + "\n";
          }
          message = $localize`:@@PlanungAPI_Task_ContextMenu_NoCanDo_Text::\r\n\r\n${message}\r\nPlease edit these tasks in their detail views.`;
          return this._dialogs.Information(
            message,
            $localize`:@@PlanungAPI_Task_ContextMenu_NoCanDo_Title:Changes failed`,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public OnAnlageSelect(result: AnlageViewModel) {
    if (!result && result !== null) return;

    if (result === null) this.filter.AnlageNr = null;
    else if ("anlageNrIntern" in result)
      this.filter.AnlageNr = result.anlageNrIntern;

    this._changeDetector.detectChanges();
  }

  public onPlanungTabSelected(event: any) {
    this.uiState.ReferenceIndex = null;
    this.uiState.PlanView = event.index;

    this._changeDetector.markForCheck();
  }

  private resolveAssignedToID(
    param: string | undefined,
  ): string | null | undefined {
    return param === undefined ? undefined : param === "null" ? null : param;
  }

  private mapAndSaveTaskChanges(
    tasks: ITaskDto[],
    mapper: (task: ITaskDto) => ITaskDto | null,
    refresh: boolean,
  ): void {
    this._zone.run(() => {
      const mapped = tasks.map(mapper).filter((x) => x);
      this.SaveTaskChanges(mapped, refresh).subscribe();
    });
  }

  public SetTaskAssignedTo(
    tasks: ITaskDto[],
    newAssignedToIDParam: string,
    refresh: boolean,
  ) {
    const newAssignedToID = this.resolveAssignedToID(newAssignedToIDParam);

    this.mapAndSaveTaskChanges(
      tasks,
      (task) => {
        if (task.assignedToResourceID === newAssignedToID) {
          return null;
        }
        if (newAssignedToID !== undefined) {
          task.assignedToResourceID = newAssignedToID;
        }
        return task;
      },
      refresh,
    );
  }

  public SetTaskPeriodOfAction(
    tasks: ITaskDto[],
    period: YearPeriod,
    refresh: boolean,
    newAssignedToIDParam?: string,
  ) {
    const newAssignedToID = this.resolveAssignedToID(newAssignedToIDParam);

    this.mapAndSaveTaskChanges(
      tasks,
      (task) => {
        if (!task.dateRanges) {
          task.dateRanges = new TaskTimePeriodsDto();
        }
        task.dateRanges.periodOfAction ??= new YearPeriod();
        const d = this._dts.GetDateFromYearPeriod(period);
        if (
          task.dateRangesActive &&
          task.dateRanges.periodOfAction &&
          task.dateRanges.periodOfAction.year === d.getFullYear() &&
          task.dateRanges.periodOfAction.month === d.getMonth() + 1 &&
          newAssignedToID !== undefined &&
          task.assignedToResourceID === newAssignedToID
        ) {
          return null;
        }
        if (
          task.dateRanges.periodOfAction.year === d.getFullYear() &&
          task.dateRanges.periodOfAction.month === d.getMonth() + 1 &&
          newAssignedToID !== undefined &&
          task.assignedToResourceID !== newAssignedToID &&
          task.dateRanges.todoDate
        ) {
          d.setFullYear(
            task.dateRanges.todoDate.getFullYear(),
            task.dateRanges.todoDate.getMonth(),
            task.dateRanges.todoDate.getDate(),
          );
        }
        task.dateRanges.periodOfAction = period;
        task.dateRanges.plannedWeek = this._dts.GetCalendarWeekFromDate(d);
        task.dateRanges.todoDate = d;
        task.dateRangesActive = true;
        if (newAssignedToID !== undefined) {
          task.assignedToResourceID = newAssignedToID;
        }
        return task;
      },
      refresh,
    );
  }

  public SetTaskPlannedWeek(
    tasks: ITaskDto[],
    cw: CalendarWeek,
    refresh: boolean,
    newAssignedToIDParam?: string,
  ) {
    const newAssignedToID = this.resolveAssignedToID(newAssignedToIDParam);

    this.mapAndSaveTaskChanges(
      tasks,
      (task) => {
        if (!task.dateRanges) {
          task.dateRanges = new TaskTimePeriodsDto();
        }
        task.dateRanges.plannedWeek ??= new CalendarWeek();
        if (cw) {
          const dayOfWeek = this._dts.GetDateFromCalendarWeek(cw);
          const period = new YearPeriod({
            year: dayOfWeek.getFullYear(),
            month: dayOfWeek.getMonth() + 1,
          });
          if (
            task.dateRangesActive &&
            task.dateRanges.plannedWeek &&
            task.dateRanges.plannedWeek.year === cw.year &&
            task.dateRanges.plannedWeek.week === cw.week &&
            newAssignedToID !== undefined &&
            task.assignedToResourceID === newAssignedToID
          ) {
            return null;
          }
          task.dateRanges.periodOfAction = period;
          if (
            task.dateRanges.plannedWeek.year === cw.year &&
            task.dateRanges.plannedWeek.week === cw.week &&
            newAssignedToID !== undefined &&
            task.assignedToResourceID !== newAssignedToID &&
            task.dateRanges.todoDate
          ) {
            dayOfWeek.setFullYear(
              task.dateRanges.todoDate.getFullYear(),
              task.dateRanges.todoDate.getMonth(),
              task.dateRanges.todoDate.getDate(),
            );
          }
          task.dateRanges.todoDate = dayOfWeek;
        }
        task.dateRanges.plannedWeek = cw;
        task.dateRangesActive = true;
        if (newAssignedToID !== undefined) {
          task.assignedToResourceID = newAssignedToID;
        }
        return task;
      },
      refresh,
    );
  }

  public SetTaskTodoDate(
    tasks: ITaskDto[],
    todo: Date,
    refresh: boolean,
    newAssignedToIDParam?: string,
  ) {
    const newAssignedToID = this.resolveAssignedToID(newAssignedToIDParam);

    this.mapAndSaveTaskChanges(
      tasks,
      (task) => {
        if (!task.dateRanges) {
          task.dateRanges = new TaskTimePeriodsDto();
        }
        if (todo) {
          if (
            task.dateRangesActive &&
            task.dateRanges.todoDate.getFullYear() === todo.getFullYear() &&
            task.dateRanges.todoDate.getMonth() === todo.getMonth() &&
            task.dateRanges.todoDate.getDate() === todo.getDate() &&
            newAssignedToID !== undefined &&
            task.assignedToResourceID === newAssignedToID
          ) {
            return null;
          }
          const period = new YearPeriod({
            year: todo.getFullYear(),
            month: todo.getMonth() + 1,
          });
          task.dateRanges.periodOfAction = period;
          task.dateRanges.plannedWeek =
            this._dts.GetCalendarWeekFromDate(todo);
        }
        task.dateRanges.todoDate = todo;
        if (newAssignedToID !== undefined) {
          task.assignedToResourceID = newAssignedToID;
        }
        task.dateRangesActive = true;
        return task;
      },
      refresh,
    );
  }

  public OnTaskContextMenuItemClicked(event: any) {
    if (!event.item.click) return;
    event.item.click();
  }
  public OnTaskOpen(taskNo: number): void {
    this._nav.navigateTo_Planung_Tasks(taskNo);
  }

  public OnTasksPatch(tasks: ITaskDto[], data: any) {
    if (!tasks || !data) {
      return;
    }

    const arrOriginal = [];
    const arrPatch = [];
    for (const x of tasks) {
      const lastChanged = new Date(x.changed);

      const original = { id: x.id, lastChanged };
      const patch = { id: x.id, lastChanged };
      for (const key of Object.keys(data)) {
        original[key] = x[key];
        patch[key] = data[key];
      }
      arrOriginal.push(original);
      arrPatch.push(patch);
    }
    const arrDiff = [];
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < arrOriginal.length; ++i) {
      const diff = jsonpatch.compare(
        arrOriginal[i],
        arrPatch[i],
        false,
      ) as Operation[];
      for (const op of diff) {
        op.path = "/" + arrOriginal[i].id + op.path;
      }
      arrDiff.push(...diff);
    }

    if (arrDiff.length === 0) {
      return;
    }

    this.isLoading.next(true);
    this._taskClient
      .patchBulk(arrDiff)
      .pipe(
        finalize(() => this.ReloadTasks()),
        catchError((err) => {
          this._HandleError(err);
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public canTasksDelete() {
    return this._perm.UserCan_PlanungAPI_TaskArchive;
  }

  public OnTasksDelete(tasks: ITaskDto[]): void {
    if (!this.canTasksDelete()) {
      return;
    }
    this._dialogs
      .AskYesCancelPromptString(
        $localize`:@@Dialogs_PlanungAPI_Tasks_ConfirmDeletion:Do you really want to delete the selected tasks?`,
        $localize`:@@Dialog_Title_ConfirmDeletion:Confirm Deletion`,
      )
      .subscribe((result) => {
        if (result === CstDialogResult.Cancel || typeof result !== "string") {
          return;
        }

        this.isLoading.next(true);

        const command = new TasksDeleteMultiCommand({
          taskIDs: tasks.map((t) => t.id),
          archivedReason: result,
        });

        this._taskClient
          .deleteMulti(command)
          .pipe(
            finalize(() => this.ReloadTasks()),
            catchError((err) => {
              console.error(err);
              this.ReloadTasks();
              return EMPTY;
            }),
            takeUntil(this.destroy$),
          )
          .subscribe();
      });
  }

  public OnTasksDragStart(e: DragEvent, tasks: TaskDto[]) {
    e.dataTransfer.setData(this.DragDropKey_tasks, JSON.stringify(tasks));

    const div = document.body.appendChild(document.createElement("div"));
    this.dummyDragImage = div;
    div.style.position = "absolute";
    div.style.top = "-10000px";
    div.style.background = "gainsboro";
    div.style.border = "1px solid gray";
    div.style.padding = "10px";
    tasks.forEach((t, idx) => {
      const span = div.appendChild(document.createElement("span"));
      span.innerHTML = t.taskNoDisplay + ": " + t.label + "<br>";
      if (idx < tasks.length - 1) {
        span.innerHTML += "<hr>";
      }
    });
    e.dataTransfer.setDragImage(div, 0, 0);
  }

  public OnDragEndGlobal() {
    if (this.dummyDragImage) {
      document.body.removeChild(this.dummyDragImage);
      this.dummyDragImage = null;
    }

    this._changeDetector.detectChanges();
  }

  public TaskUnassign(tasks: ITaskDto[]) {
    for (const task of tasks) {
      task.dateRangesActive = false;
    }
    this.SaveTaskChanges(tasks, true).subscribe();
  }

  public SaveTaskChanges(tasks: ITaskDto[], refresh: boolean = false) {
    if (!tasks || !tasks.length) {
      return;
    }

    this.isLoading.next(true);

    const hasDiffClaimID = tasks.some(
      (t) =>
        t &&
        t?.dateRangesActive &&
        t?.claimResourceID &&
        t?.assignedToResourceID !== t.claimResourceID,
    );

    const commands = tasks.map(
      (task) =>
        new TaskUpdateDateRangesCommand({
          taskID: task.id,
          assignedToResourceID: task.assignedToResourceID,
          dateRangesActive: task.dateRangesActive,
          dueDate: task.dueDate,
          ...task.dateRanges,
        }),
    );

    const dueDateInfos: string[] = [];
    const opts = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    } as Intl.DateTimeFormatOptions;

    let todoDate: string = null;
    for (const task of tasks) {
      const todo = this._dts.DateToInteger(task?.dateRanges?.todoDate, false);
      const due = this._dts.DateToInteger(task?.dueDate, true);

      if (todo <= due) {
        continue;
      }

      todoDate = task.dateRanges.todoDate.toLocaleString(undefined, opts);
      const dueDate = task.dueDate.toLocaleString(undefined, opts);
      dueDateInfos.push(
        $localize`:@@Dialogs_PlanungAPI_Ask_PlanningAfterDueDate_Entry:${task.taskNoDisplay} - ${task.label} - Due Date: ${dueDate}`,
      );
    }

    let obs: Observable<TaskUpdateDateRangesCommand[]> = null;
    if (dueDateInfos.length > 0) {
      obs = this._dialogs
        .AskYesCancel(
          $localize`:@@Dialogs_PlanungAPI_Ask_PlanningAfterDueDate_Text:At least one of the tasks is now planned for a date (${todoDate}) which comes after its due date.\nShould the due date be set to the todo date?\nOtherwise this operation can not be performed. ` +
            "\n- " +
            dueDateInfos.join("\n- "),
        )
        .pipe(
          switchMap((result) => {
            if (result !== CstDialogResult.Yes) {
              return EMPTY;
            }

            for (const task of tasks) {
              const todo = this._dts.DateToInteger(
                task?.dateRanges?.todoDate,
                false,
              );
              const due = this._dts.DateToInteger(task?.dueDate, true);
              if (todo <= due) {
                continue;
              }

              const cmd = commands.find((x) => x.taskID === task.id);
              if (!cmd) {
                continue;
              }

              cmd.dueDate = task.dateRanges.todoDate;
              task.dueDate = cmd.dueDate;
            }
            return of(commands);
          }),
        );
    } else {
      obs = of(commands);
    }

    return obs.pipe(
      switchMap((cmds) => {
        return this._taskClient.updateDateRangesBulk(cmds).pipe(
          finalize(() => this.isLoading.next(false)),
          catchError((err) => {
            this._HandleError(err);
            return EMPTY;
          }),
          map((result) => {
            if (refresh) {
              this.ReloadTasks();
            }
            // If there is any updated task, which is not inside the tasks list,
            // then the task is planned but not displayed, because the filter for all tasks does not include it.
            // So show a snackbar to inform the user about that.
            if (tasks.some((t) => t && !this.filter.apply(t))) {
              this._alrt
                .showSnackbar(
                  $localize`:@@Dialogs_PlanungAPI_Snackbar_PlannedButTaskNotInFilter:Task was planned but is not in filter and therefore not displayed`,
                  { duration: "long", type: "info" },
                )
                .subscribe();
            }
            if (hasDiffClaimID) {
              this._dialogs.Information(
                $localize`:@@Dialogs_PlanungAPI_Info_TaskAlreadyClaimed:At least one task in the selection is already claimed.
The tasks involved have been planned for the new user,
but they are each still displayed by the user who has claimed it.`,
              );
            }
            return result;
          }),
          takeUntil(this.destroy$),
        );
      }),
    );
  }

  private _HandleError(err: any) {
    if (err instanceof ValidationResult) {
      this._HandleError_ValidationResult(err);
      return;
    }

    if (err instanceof InternalServerErrorResult) {
      this._HandleError_InternalServerErrorResult(err);
      return;
    }

    if (err.message || err.error || typeof err === "string") {
      const msg = typeof err === "string" ? err : (err.message ?? err.error);
      this._dialogs
        .Warning(`Es ist ein Fehler aufgetreten:\r\n${msg}`)
        .subscribe();
      return;
    }

    this._dialogs
      .Warning("Es ist ein unerwarteter Fehler aufgetreten")
      .subscribe();
  }

  private _HandleError_ValidationResult(result: ValidationResult) {
    this._dialogs
      .ErrorValidation(result.message, result)
      .pipe(
        /// Trotzdem die Aufgaben neu laden, kann ja sein dass es dennoch ein paar Aufgaben gibt, die aktualisiert wurden
        finalize(() => this.ReloadTasks()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private _HandleError_InternalServerErrorResult(
    err: InternalServerErrorResult,
  ) {
    this._dialogs.DisplayInternalServerError(err);
  }
}
